import dynamic from 'next/dynamic'
import { WfsPage, tenant2, FdrPage, FfnPage } from 'utils/environmentConfig'
import Image from 'next/image'
import { AchieveLink } from 'components/AchieveLink'
import { mainProductConfig } from 'services/contentful/helpers'
import { useContext } from 'react'
import { LayoutContext } from 'providers/LayoutProvider'
import { Typography as OwnerTypography } from 'components/Contentful'
import { Button } from '@achieve/ascend'
import { SignInLink } from 'components/SignInLink'
import useMobileNavState from 'hooks/useMobileNavState'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import { handleTrackAndReactEvent } from 'utils/analytics'
import MenuIcon from 'components/Svgs/Menu.js'
import Close from 'components/Svgs/Close.js'
import useIsSsr from 'hooks/useIsSsr'
import classNames from 'classnames'
import Navigation from 'components/Navigation'
import { PhoneDataContext } from 'providers/PhoneDataProvider'
import HeaderCta from 'components/Header/HeaderCta'

const AchieveLogoSVG = dynamic(() => import('components/Svgs/AchieveLogoSVG.js'))
const Website2LogoSVG = dynamic(() => import('components/Svgs/Website2LogoSVG.js'))
const Website2LogoWhiteSVG = dynamic(() => import('components/Svgs/Website2LogoWhiteSVG.js'))
const Website3LogoSVG = dynamic(() => import('components/Svgs/Website3LogoSVG.js'))
const CurrentSvg = ({ siteEnv, ...props }) => {
  switch (siteEnv) {
    case WfsPage:
      return (
        <Image
          src="/wfs-logo-dark.png"
          title="WFS Logo"
          alt="WFS Logo"
          width={246}
          height={87}
          {...props}
        />
      )
    case tenant2:
      return <Website2LogoSVG title="website-tenant-2" alt="website-tenant-2" {...props} />
    case FdrPage:
      return <Website2LogoSVG title="FDR Logo" alt="FDR Logo" {...props} />
    case FfnPage:
      return <Website3LogoSVG title="FFN Logo" alt="FFN Logo" {...props} />
    default:
      return <AchieveLogoSVG title="Achieve Logo" alt="Achieve Logo" {...props} />
  }
}

export default function HeaderMobileLeft({
  logoEvent,
  signInLinkUrl,
  signInLinkText,
  linkUrl,
  linkText,
  disabledRoutes,
  linkEvent,
  mainNavigation,
  disabledElements,
}) {
  const { state: { siteEnv } = {} } = useContext(LayoutContext)
  const [mobileNavOpen, setMobileNavOpen] = useMobileNavState()
  const { fireAndForget } = useTriggerEvents()
  const isSsr = useIsSsr()
  let phoneData = useContext(PhoneDataContext)

  const callToAssociatedNumber = () => {
    if (phoneData?.phoneNumber) window.open(`tel:${phoneData.phoneNumber}`, '_self')
  }

  const handleCloseClick = (event, track) => {
    fireAndForget({
      event_type: track?.event_type,
      ...handleTrackAndReactEvent(event, track),
    })
    !isSsr && window.scrollTo({ top: 0, behavior: 'instant' })
    setMobileNavOpen(false)
  }

  const handleOpenClick = (event, track) => {
    fireAndForget({
      event_type: track?.event_type,
      ...handleTrackAndReactEvent(event, track),
    })
    setMobileNavOpen(true)
  }

  const CallUsButton = () => (
    <AchieveLink
      noLink
      track={{
        list_name: 'CALL STICKY HEADER',
        click_id: phoneData?.phoneNumber,
        click_text: `Call us`,
        click_url: phoneData?.phoneNumber,
        click_type: 'Call us Click',
        event_action: 'button_click',
        nav_link_section: 'Sticky Header',
        track_event: 'call-sticky- hidden lg:block',
      }}
      data-testid="call-sticky hidden lg:block"
    >
      <Button
        color="secondary"
        variant="contained"
        className={classNames('flex flex-row gap-2', { 'whitespace-nowrap': siteEnv === FdrPage })}
        size="small"
        onClick={() => callToAssociatedNumber()}
      >
        <OwnerTypography variant="displayXxs" fontWeight="bold" content="Call Us" />
      </Button>
    </AchieveLink>
  )

  const HeaderCtaButton = () => (
    <HeaderCta
      linkUrl={linkUrl}
      linkText={linkText}
      size="small"
      data-testid="header-get-started-button"
    />
  )
  const DashboardCtaButton = () => (
    <SignInLink
      disabledRoutes={disabledRoutes}
      data-testid="mobile-nav-sign-in-link"
      signInLinkText={signInLinkText}
      signInLinkUrl={signInLinkUrl}
      outlined={siteEnv === FdrPage}
    />
  )

  const getSubNav = () => {
    return (
      <div className="flex flex-col gap-4 px-2 py-6 text-center justify-center items-center">
        <DashboardCtaButton />
        <HeaderCtaButton />
        <div className="flex flex-col gap-4 items-center">
          <div className="flex flex-col gap-2">
            <OwnerTypography
              variant="bodySm"
              fontWeight="regular"
              content="Talk to a certified debt consultant"
            />
            <OwnerTypography
              variant="bodyMd"
              fontWeight="regular"
              content={phoneData?.phoneNumber}
            />
          </div>
          <CallUsButton />
        </div>
      </div>
    )
  }

  return (
    <div className="p-2">
      <header
        className={classNames(
          'flex flex-row justify-between items-center gap-4 transition-transform duration-300 ease-in-out transform',
          {
            'translate-x-[17rem]': mobileNavOpen,
          }
        )}
      >
        <div className="flex flex-col justify-center items-center">
          {!mobileNavOpen && (
            <button
              onClick={(e) => {
                handleOpenClick(e, {
                  nav_link_section: 'Toggle menu',
                  click_type: 'Button Click',
                  click_id: 'open button',
                })
              }}
              className="border-none bg-transparent"
              data-testid="mobile-nav-open-button"
              aria-label="Main navigation menu button"
            >
              <MenuIcon />
            </button>
          )}
          {mobileNavOpen && (
            <button
              onClick={(e) => {
                handleCloseClick(e, {
                  nav_link_section: 'Toggle menu',
                  click_type: 'Button Click',
                  click_id: 'close button',
                })
              }}
              className="border-none bg-transparent"
              role="button"
              data-testid="mobile-nav-close-button"
              aria-label="Main navigation menu close button"
            >
              <Close title="Close button" width={18} height={18} />
            </button>
          )}
        </div>
        <div className="w-[90px]">
          <AchieveLink
            track={{
              ...{
                list_name: 'Header Logo',
                click_id: `${mainProductConfig[siteEnv || 'achieve']?.['HeaderLogo']}`,
                click_text: `${
                  mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
                } | Header logo`,
                nav_link_section: 'Header logo',
                track_event: 'global_header',
                event_action: 'link_click',
                event_type: 'link_click',
              },
              ...logoEvent,
            }}
            href="/"
            withNextLink
            className={''}
            data-testid={`${String(
              mainProductConfig[siteEnv || 'achieve']?.['MainName']
            )?.toLowerCase()}-header-logo`}
          >
            <CurrentSvg siteEnv={siteEnv} />
          </AchieveLink>
        </div>
        <div>
          <CallUsButton />
        </div>
        <div>
          <HeaderCtaButton />
        </div>
      </header>
      <nav
        className={classNames(
          'min-w-[17rem] w-[17rem] bg-white h-[100vh] absolute top-0 left-0 overflow-hidden z-10 transition-transform duration-300 ease-in-out transform',
          { 'translate-x-[-17rem]': !mobileNavOpen, 'translate-x-0': mobileNavOpen }
        )}
      >
        <div className="flex flex-row bg-[#305a78] p-3">
          <Website2LogoWhiteSVG width={80} />
        </div>
        <Navigation
          event={linkEvent}
          mobileNavOpen={true}
          items={mainNavigation}
          disabledRoutes={disabledRoutes}
          disabledElements={disabledElements}
          mobileSubnav={getSubNav()}
          className={'py-0 min-w-[17rem] w-full overflow-hidden'}
          titleVariant={'bodySm'}
          titleMobileVariant={'bodySm'}
          subNavClassName={'bg-gray-100 shadow-inner'}
          titleClassName={'!font-light'}
          subNavTitleClassName={'!font-light'}
        />
      </nav>
    </div>
  )
}
